// src/components/ShareTestGenerator.jsx
// Quick test tool to generate albums of various sizes and test sharing

import React, { useState } from 'react';
import { shareAsStory, previewStoryLocally } from '../utils/share';

// Generate a minimal test album
function generateTestAlbum(size = 'tiny') {
  const configs = {
    tiny: { pages: 1, photosPerPage: 1 },
    small: { pages: 3, photosPerPage: 2 },
    medium: { pages: 10, photosPerPage: 3 },
    large: { pages: 30, photosPerPage: 4 },
    huge: { pages: 100, photosPerPage: 5 }
  };

  const config = configs[size] || configs.tiny;
  
  // Use a tiny 1x1 data URL to keep payload minimal
  const tinyPhoto = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==";
  
  const pages = [];
  for (let i = 0; i < config.pages; i++) {
    const photos = [];
    for (let j = 0; j < config.photosPerPage; j++) {
      photos.push({
        id: `photo-${i}-${j}`,
        src: tinyPhoto,
        url: tinyPhoto,
        alt: `Test photo ${i}-${j}`
      });
    }

    pages.push({
      type: 'standard',
      templateKey: 'photo-grid',
      caption: `Test page ${i + 1}`,
      photos: photos,
      items: photos.map(p => ({
        photoId: p.id,
        url: p.url,
        position: { x: 50, y: 50, scale: 100 }
      }))
    });
  }

  return {
    trip: {
      name: `Test Album - ${size}`,
      dates: 'Test Date Range',
      route: 'Test Location 1 → Test Location 2'
    },
    pages: pages,
    photos: pages.flatMap(p => p.photos)
  };
}

export default function ShareTestGenerator() {
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const runTest = async (size, action = 'preview') => {
    setLoading(true);
    setTestResult(null);

    try {
      console.log(`\n${'='.repeat(60)}`);
      console.log(`🧪 SHARE TEST: ${size.toUpperCase()} ALBUM - ${action.toUpperCase()}`);
      console.log(`${'='.repeat(60)}\n`);

      const album = generateTestAlbum(size);
      const json = JSON.stringify(album);
      const sizeKB = (json.length / 1024).toFixed(2);
      
      console.log('📊 Test Album Stats:', {
        size,
        pages: album.pages.length,
        photos: album.photos.length,
        jsonLength: json.length,
        sizeKB: `${sizeKB} KB`
      });

      const startTime = Date.now();

      let result;
      if (action === 'preview') {
        await previewStoryLocally(album);
        result = { success: true, action: 'preview', link: 'opened in new tab' };
      } else {
        result = await shareAsStory(album, `Test Album - ${size}`);
      }

      const duration = Date.now() - startTime;

      console.log('✅ Test Result:', {
        ...result,
        duration: `${duration}ms`
      });

      setTestResult({
        success: result.success,
        size,
        pages: album.pages.length,
        sizeKB,
        duration,
        link: result.link,
        strategy: result.strategy,
        action
      });

    } catch (err) {
      console.error('❌ Test Failed:', err);
      setTestResult({
        success: false,
        size,
        error: err.message,
        stack: err.stack
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h2 className="text-2xl font-bold text-white mb-4">🧪 Share Test Generator</h2>
        <p className="text-slate-300 mb-6">
          Test sharing with albums of different sizes using minimal data URLs
        </p>

        <div className="grid grid-cols-2 gap-3">
          {['tiny', 'small', 'medium', 'large', 'huge'].map(size => (
            <div key={size} className="space-y-2">
              <button
                onClick={() => runTest(size, 'preview')}
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white px-4 py-3 rounded-lg font-medium transition-colors"
              >
                {size.toUpperCase()} - Preview
              </button>
              <button
                onClick={() => runTest(size, 'share')}
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-500 disabled:bg-slate-700 text-white px-4 py-3 rounded-lg font-medium transition-colors"
              >
                {size.toUpperCase()} - Share
              </button>
            </div>
          ))}
        </div>

        {loading && (
          <div className="mt-6 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="text-slate-400 mt-2">Testing...</p>
          </div>
        )}

        {testResult && (
          <div className={`mt-6 p-4 rounded-lg ${
            testResult.success ? 'bg-green-900/30 border border-green-700' : 'bg-red-900/30 border border-red-700'
          }`}>
            <h3 className="font-bold text-white mb-2">
              {testResult.success ? '✅ Test Passed' : '❌ Test Failed'}
            </h3>
            <div className="text-sm space-y-1">
              <div className="text-slate-300">
                <span className="font-semibold">Size:</span> {testResult.size} ({testResult.pages} pages, {testResult.sizeKB} KB)
              </div>
              {testResult.duration && (
                <div className="text-slate-300">
                  <span className="font-semibold">Duration:</span> {testResult.duration}ms
                </div>
              )}
              {testResult.strategy && (
                <div className="text-slate-300">
                  <span className="font-semibold">Strategy:</span> {testResult.strategy}
                </div>
              )}
              {testResult.link && (
                <div className="text-slate-300">
                  <span className="font-semibold">Link:</span>{' '}
                  <a href={testResult.link} target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">
                    {testResult.link.substring(0, 80)}...
                  </a>
                </div>
              )}
              {testResult.error && (
                <div className="text-red-300">
                  <span className="font-semibold">Error:</span> {testResult.error}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h3 className="text-xl font-bold text-white mb-4">📋 Test Sizes Reference</h3>
        <div className="space-y-2 text-sm">
          <div className="text-slate-300"><span className="font-semibold">Tiny:</span> 1 page, 1 photo (~1-2 KB) - Should use inline hash</div>
          <div className="text-slate-300"><span className="font-semibold">Small:</span> 3 pages, 6 photos (~3-5 KB) - Should use inline hash</div>
          <div className="text-slate-300"><span className="font-semibold">Medium:</span> 10 pages, 30 photos (~15-20 KB) - Should use inline hash</div>
          <div className="text-slate-300"><span className="font-semibold">Large:</span> 30 pages, 120 photos (~50-70 KB) - Should use inline hash</div>
          <div className="text-slate-300"><span className="font-semibold">Huge:</span> 100 pages, 500 photos (~200-300 KB) - Should use inline hash or Firebase</div>
        </div>
        <div className="mt-4 p-3 bg-yellow-900/30 border border-yellow-700 rounded">
          <p className="text-yellow-200 text-sm">
            ⚠️ All tests use tiny 1x1 data URLs to keep payloads minimal. Real photos will be much larger!
          </p>
        </div>
      </div>

      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h3 className="text-xl font-bold text-white mb-4">🔍 How to Use</h3>
        <ol className="space-y-2 text-sm text-slate-300 list-decimal list-inside">
          <li>Start with <span className="font-semibold text-white">TINY - Preview</span> to test basic functionality</li>
          <li>Check browser console for detailed logs</li>
          <li>Gradually increase size to find where issues occur</li>
          <li>Use "Preview" to test without sharing UI</li>
          <li>Use "Share" to test the full share flow</li>
        </ol>
      </div>
    </div>
  );
}
